import { useEffect, useState, useMemo } from 'react';
import _isEqual from 'lodash/isEqual';
import _noop from 'lodash/noop';
import compose from 'recompose/compose';

import { withFormik } from 'formik';
import { withStore } from 'store';

import { Modal } from 'components';

import { setGiftsProductsParams } from 'store/actions/gifts';
import { transformForSelect } from 'util/transformHelpers';
import { DISCOUNTS } from 'constants/values/discounts';
import { useFirstRender } from 'hooks';

import trash from 'assets/img/trash.svg';

import Button from 'react-bootstrap/Button';
import FilterSelect from '../FilterSelect';
import RatingFilter from '../RatingFilter';
import PriceFilter from '../PriceFilter';

import {
  mapPropsToValues,
  FIELDS_TO_CLEAN,
  RECOMMENDATIONS_FIELDS,
  FILTER_CATALOG_FIELDS
} from './formikProps';
import { mapStateToProps, mapDispatchToProps } from './storeProps';
import propTypes from './propTypes';
import styles from './styles.module.scss';

function FilterWidget({
  dispatch,
  filters,
  values,
  showRating = true,
  setFieldValue,
  isModal = false,
  show,
  onClose = _noop,
  onFilterChange = _noop,
  onCategoryChange = _noop,
  onFilterSubmit = _noop,
  giftsParams,
  showRecommendations,
  recommendedCategories = [],
  selectedCategory = {},
  getHelpers = _noop,
  onCleanFilters = _noop,
  initialValues,
  cleanGiftsProductsParams = _noop,
  firstCategory,
  setFirstCategory = _noop,
  priceFilterCanBeSent
}) {
  const [prevGiftsParams, setPrevGiftsParams] = useState(giftsParams);
  const [maxDiscount, setMaxDiscount] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [prevCategory, setPrevCategory] = useState(null);
  const [hasAppliquedFilter, setHasApliquedFilter] = useState(false);

  const isFirstRender = useFirstRender();

  const { sections = [], brands = [], discounts = [] } = filters;
  const { brand, discount, highRating, minPrice, maxPrice } = values;

  const pricesHasChanged = useMemo(() => {
    const { defaultMinPrice, defaultMaxPrice } = values;
    return (
      !_isEqual(defaultMinPrice, minPrice) ||
      !_isEqual(defaultMaxPrice, maxPrice)
    );
  }, [minPrice, maxPrice, values.defaultMinPrice, values.defaultMaxPrice]);

  const paramsHasChanged = useMemo(
    () => !_isEqual(prevGiftsParams, giftsParams),
    [giftsParams, prevGiftsParams]
  );

  const handleApplyFilters = () => {
    priceFilterCanBeSent(pricesHasChanged);
    setHasSubmitted(paramsHasChanged || pricesHasChanged);

    onFilterSubmit(giftsParams);

    setHasApliquedFilter(true);
  };

  useEffect(() => {
    if (hasSubmitted) {
      onFilterChange(giftsParams);
      setHasSubmitted(false);
      priceFilterCanBeSent(false);
    }
  }, [hasSubmitted, giftsParams]);

  const handleCategoryChange = (evt) => {
    onCategoryChange(evt.target.value);
  };

  const handleSectionChange = (evt) => {
    dispatch(setGiftsProductsParams({ section: evt.target.value }));
  };

  const handleCleanFilters = () => {
    if (!setFieldValue) return;

    onCleanFilters();

    if (showRecommendations) {
      setFieldValue('category', firstCategory);
      handleCategoryChange({ target: { value: firstCategory } });
    } else {
      FIELDS_TO_CLEAN.forEach((field) => {
        let initialValue = initialValues[field];
        // eslint-disable-next-line no-shadow
        let pricesHasChanged = false;

        if (field === 'minPrice') {
          initialValue = values.defaultMinPrice;
          pricesHasChanged = true;
        }

        if (field === 'maxPrice') {
          initialValue = values.defaultMaxPrice;
          pricesHasChanged = true;
        }

        setFieldValue(field, initialValue);

        if (pricesHasChanged && hasAppliquedFilter) {
          priceFilterCanBeSent(true);
          setHasSubmitted(true);
          onFilterSubmit(giftsParams);
        }
      });
      cleanGiftsProductsParams();
    }
  };

  useEffect(() => {
    if (isFirstRender) return;
    dispatch(
      setGiftsProductsParams({
        ...values,
        category: giftsParams.category
      })
    );
  }, [brand, highRating, discount, minPrice, maxPrice, giftsParams.category]);

  const helpers = useMemo(() => ({ setFieldValue }), [setFieldValue]);

  useEffect(() => {
    setPrevGiftsParams(giftsParams);
  }, [giftsParams]);

  useEffect(() => {
    if (isFirstRender || _isEqual(giftsParams, prevGiftsParams)) return;
    onFilterChange(giftsParams);
  }, [
    showRecommendations ? giftsParams.category : giftsParams.section,
    giftsParams.brand,
    giftsParams.highRating,
    giftsParams.discount,
    giftsParams.sort
  ]);

  useEffect(() => {
    getHelpers(helpers);
  }, [getHelpers, helpers]);

  useEffect(() => {
    if (!showRecommendations && !isFirstRender && giftsParams.category) {
      handleCleanFilters();
    }
  }, [showRecommendations, giftsParams.category]);

  useEffect(() => {
    if (discounts.length) {
      setMaxDiscount(
        parseInt(
          discounts.sort((a, b) =>
            parseInt(a.value, 10) < parseInt(b.value, 10) ? 1 : -1
          )[0].value,
          10
        )
      );
    } else {
      setMaxDiscount(0);
    }
  }, [discounts]);

  useEffect(() => {
    if (!values.category && selectedCategory.id) {
      setFieldValue('category', selectedCategory.id);
      setFirstCategory(selectedCategory.id);
    }
  }, [values.category, selectedCategory.id]);

  const showCleanFilters = useMemo(() => {
    const listToCheck = showRecommendations
      ? RECOMMENDATIONS_FIELDS
      : FILTER_CATALOG_FIELDS;

    const fieldWithValue = Object.entries(values).some(([key, value]) => {
      if (listToCheck.includes(key)) {
        if (key === 'minPrice') {
          return value !== values.defaultMinPrice;
        }
        if (key === 'maxPrice') {
          return value !== values.defaultMaxPrice;
        }
        if (showRecommendations && key === 'category') {
          return value !== firstCategory;
        }

        return Boolean(value);
      }

      return false;
    });

    return fieldWithValue;
  }, [showRecommendations, values]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleApplyFilters();
  };

  const persistFilter = true;

  const renderContent = (
    <section className={`${styles.FilterWidget} mb-3`}>
      <form onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between">
          <div className={`py-2 ${styles.titleFirst}`}>Filtros</div>
          {showCleanFilters && (
            <div
              role="button"
              tabIndex={0}
              onClick={handleCleanFilters}
              onKeyPress={handleCleanFilters}
              className={`${styles.trash} d-flex align-items-center mb-2`}
            >
              <img
                src={trash}
                className={styles.trashIcon}
                alt="Limpiar filtros"
              />
            </div>
          )}
        </div>

        {showRating && !showRecommendations && (
          <RatingFilter className={styles.borderTop} />
        )}

        {showRecommendations ? (
          <div className={`py-2 ${styles.borderTop} text-left`}>
            <p className={styles.title}>Categorías</p>
            <div className={styles.criteriaList}>
              <FilterSelect
                items={transformForSelect(recommendedCategories, 'id', 'name')}
                name="category"
                onChange={handleCategoryChange}
                isRemovable={false}
                persist={persistFilter}
              />
            </div>
          </div>
        ) : (
          <>
            <div className={`py-2 ${styles.borderTop}`}>
              <p className={styles.title}>Departamentos</p>
              <div className={styles.criteriaList}>
                <FilterSelect
                  items={transformForSelect(sections, 'id', 'name')}
                  name="section"
                  onChange={handleSectionChange}
                  persist={persistFilter}
                />
              </div>
            </div>

            <div className={`py-2 ${styles.borderTop}`}>
              <p className={styles.title}>Marca</p>
              <div className={styles.criteriaList}>
                <FilterSelect
                  items={transformForSelect(brands, 'value', 'name')}
                  name="brand"
                  persist={persistFilter}
                />
              </div>
            </div>

            <div className={`py-2 ${styles.borderTop}`}>
              <p className={styles.title}>Descuento</p>
              <div className={styles.criteriaList}>
                <FilterSelect
                  name="discount"
                  items={DISCOUNTS.filter(
                    (item) => parseInt(item.value, 10) <= maxDiscount
                  )}
                  persist={persistFilter}
                />
              </div>
            </div>

            <div className={`py-2 ${styles.borderTop}`}>
              <p className={styles.title}>Precio</p>
              <div className={styles.criteriaList}>
                <PriceFilter
                  prevCategory={prevCategory}
                  setPrevCategory={setPrevCategory}
                  setHasApliquedFilter={setHasApliquedFilter}
                />
              </div>
            </div>

            <Button block className="my-4" type="submit">
              Aplicar filtros
            </Button>
          </>
        )}
      </form>
    </section>
  );

  return (
    <>
      {isModal ? (
        <Modal
          show={show}
          onClose={onClose}
          variant="completed"
          variantOnlyInMobile
          withActionButtons={false}
        >
          {renderContent}
        </Modal>
      ) : (
        renderContent
      )}
    </>
  );
}

FilterWidget.propTypes = propTypes;

export default compose(
  withStore({ mapStateToProps, mapDispatchToProps }),
  withFormik({ mapPropsToValues })
)(FilterWidget);
